import { useEffect, useState } from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { Icon } from 'Components';
import Alert from '../../components/Alert';
import StepFooter from '../../components/StepFooter';
import { MAX_RECIPIENTS } from "../../PhishingManager.constants";
import { getInvalidEmails, normalizeEmail } from '../../utils';

const validateEmails = (
  emails: string[],
  t: (key: string, options?: any) => string
): string | null => {
  if (emails.length > MAX_RECIPIENTS) {
    return t('phishingManager.campaignSetup.step2.validation.maxRecipients', {
      count: MAX_RECIPIENTS
    });
  }
  const invalidEmails = getInvalidEmails(emails);
  if (invalidEmails.length === 1 && emails.length === 1) {
    return t('phishingManager.campaignSetup.step2.validation.singleInvalid');
  }

  if (invalidEmails.length === 1) {
    return t('phishingManager.campaignSetup.step2.validation.invalidEmail', {
      email: invalidEmails[0]
    });
  }

  if (invalidEmails.length > 1) {
    const last = invalidEmails.pop();
    return t('phishingManager.campaignSetup.step2.validation.multipleInvalidEmails', {
      emails: invalidEmails.join(', '),
      lastEmail: last
    });
  }
  return null;
};

type TestConfigurationProps = {
  onBack: () => void;
  onNext: (emails: string[]) => void;
  stepErrorMessage?: string | null;
  setStepErrorMessage: (message: string | null) => void;
};

const TestConfiguration = ({
  onBack,
  onNext,
  stepErrorMessage,
  setStepErrorMessage
}: TestConfigurationProps) => {
  const { t } = useTranslation();

  const [recipients, setRecipients] = useState<string[]>([]);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');

  const tagRender = (props: any) => {
    const { label, closable } = props;

    return (
      <span className="ml-chip">
        <span className="ml-chip-text">{label}</span>
  
        {closable && (
          <span
            className="ml-chip-close"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const updateRecipients = recipients.filter((tag) => tag !== label);
              setRecipients(updateRecipients);
              setEmailError(validateEmails(updateRecipients, t));
            }}>
            <Icon type="campaignStepClose" />
          </span>
        )}
      </span>
    );
  };

  const clearExternalError = () => {
    if (stepErrorMessage) setStepErrorMessage(null);
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    clearExternalError();
    if (emailError && !stepErrorMessage) {
      setEmailError(null);
    }
  };

  const handleChange = (values: string[]) => {
    const deduped = [...new Set(values.map(normalizeEmail))];
    setRecipients(deduped);
    setSearchValue('');
    clearExternalError();
    setEmailError(validateEmails(deduped, t));
  }

  const handleDeselect = (value: string) => {
    const normalized = normalizeEmail(value);
    const trimmedSearch = normalizeEmail(searchValue);
    let updated: string[];
    if (
      trimmedSearch === normalized &&
      recipients.includes(normalized)
    ) {
      updated = [
        ...recipients.filter(
          (v) => normalizeEmail(v) !== normalized
        ),
        normalized
      ];

      setSearchValue('');
    } else {
      updated = recipients.filter(
        (v) => normalizeEmail(v) !== normalized
      );
    }

    setRecipients(updated);
    clearExternalError();
    const error = validateEmails(updated, t);
    setEmailError(error);
  };

  const handleAddTypedEmail = () => {
    if (!searchValue.trim()) return;
    const normalized = normalizeEmail(searchValue);
    const updated = [...new Set([...recipients, normalized])];
    setRecipients(updated);
    setSearchValue('');
    clearExternalError();
    setEmailError(validateEmails(updated, t));
  };

  useEffect(() => {
    if (stepErrorMessage) {
      setEmailError(stepErrorMessage);
    }
  }, [stepErrorMessage]);

  const isNextDisabled =
    !recipients.length ||
    !!emailError ||
    recipients.length > MAX_RECIPIENTS;

  const showSuggestion = Boolean(searchValue.trim());

  return (
    <>
      <div className="test-configuration-container step-card">
        <div className="card-header">
          <div className="card-title">
            {t('phishingManager.campaignSetup.step2.title')}
          </div>

          <div className="card-subtitle">
            {t('phishingManager.campaignSetup.step2.subtitle')}
          </div>
        </div>

        <div className="test-config-form">
          <label className="form-label">
            {t('phishingManager.campaignSetup.step2.testRecipients')}
            <span className="required-asterisk">*</span>
          </label>

          <div className="email-input-wrapper">
            <Select
              mode="tags"
              size="large"
              value={recipients}
              searchValue={searchValue}
              onSearch={handleSearch}
              onChange={handleChange}
              onDeselect={handleDeselect}
              placeholder={t('phishingManager.campaignSetup.step2.emailPlaceholder')}
              tokenSeparators={[',', ' ']}
              className="ml-input-chip"
              dropdownClassName="campaign-setup-filter-option-select"
              style={{ width: '100%' }}
              notFoundContent={null}
              getPopupContainer={(trigger) => trigger.parentElement}
              open={false}
              tagRender={tagRender}
            />

            {showSuggestion && (
              <div className="test-recipient-hint-card" onClick={handleAddTypedEmail}>
                <div className="test-recipient-badge">
                  {searchValue}
                </div>
                <div className="test-recipient-hint-text">
                  {t('phishingManager.campaignSetup.step2.testMessage')}
                </div>
              </div>
            )}

            {emailError && (
              <div className="ml-input-help-text">
                {emailError}
              </div>
            )}
          </div>
        </div>

        <div className="alert-section">
          <Alert
            title={t('phishingManager.campaignSetup.step3.alert.title')}
            subtitle={t('phishingManager.campaignSetup.step2.alert.subtitle')}
          />
        </div>
      </div>

      <StepFooter
        onBack={onBack}
        onNext={() => onNext(recipients)}
        nextBtnDisabled={isNextDisabled}
      />
    </>
  );
};

export default TestConfiguration;